import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Spinner,
  useConfirm,
  useToast,
} from "../../../design-system";
import { Icon } from "../../../design-system/Icon";
import useCurrentMembership from "../../../helpers/useCurrentMembership";
import {
  getWorkspaceModules,
  setWorkspaceModuleEnabled,
} from "../../../helpers/kronaly_helper";

const MODULE_DESCRIPTIONS = {
  fichaje: "Registro de jornada laboral: entrada, salida, pausas y resumen del día.",
  contratos: "Gestión de contratos de empleados, horarios y políticas de vacaciones.",
  documentos: "Documentos asociados a cada empleado (contratos firmados, nóminas, etc.).",
  documentos_internos: "Documentación corporativa: políticas, manuales, procedimientos.",
  empleados: "Listado de empleados, perfiles, equipos y estados.",
  calendario: "Calendario compartido con ausencias, vacaciones y horas extra.",
};

function unwrap(r) {
  if (r && typeof r === "object" && "data" in r) return r.data;
  return r;
}

export default function ModulosPage() {
  document.title = "Módulos | Kronaly";
  const { isAdmin, loading: membershipLoading } = useCurrentMembership();
  const toast = useToast();
  const confirm = useConfirm();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getWorkspaceModules();
      setModules(unwrap(r) || []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar los módulos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!membershipLoading && isAdmin) load();
  }, [membershipLoading, isAdmin, load]);

  const toggle = async (mod) => {
    const next = !mod.enabled;
    if (!next) {
      const ok = await confirm({
        title: `Desactivar "${mod.label}"`,
        description:
          "Las rutas del módulo dejarán de responder y la entrada se ocultará del sidebar para todos los usuarios. Los datos existentes se conservan; podrás reactivarlo cuando quieras.",
        confirmLabel: "Desactivar",
        tone: "danger",
      });
      if (!ok) return;
    }
    setSavingKey(mod.key);
    try {
      await setWorkspaceModuleEnabled(mod.key, next);
      toast.success(
        next ? "Módulo activado" : "Módulo desactivado",
        `"${mod.label}" ${next ? "ya está disponible" : "queda fuera de uso"}.`,
      );
      // Recargar /me en algún sitio sería ideal para refrescar el sidebar
      // sin recargar; por simplicidad pedimos al user que recargue.
      await load();
    } catch (e) {
      toast.error(
        "No se pudo cambiar el módulo",
        typeof e === "string" ? e : e?.message || "Error desconocido",
      );
    } finally {
      setSavingKey(null);
    }
  };

  if (membershipLoading) {
    return (
      <div className="kr-page" style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spinner />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="kr-page">
        <EmptyState
          icon="ShieldOff"
          title="Acceso restringido"
          description="Solo el propietario o un administrador del workspace pueden activar y desactivar módulos."
        />
      </div>
    );
  }

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Workspace"
        title="Módulos"
        description="Activa o desactiva las funcionalidades que tu empresa necesita. Los módulos desactivados dejan de aparecer en el sidebar y sus rutas devuelven 404."
      />

      {error && <Alert tone="danger">{error}</Alert>}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <Spinner />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {modules.map((m) => (
            <Card key={m.key} style={{ padding: 16 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: m.enabled ? "var(--kr-status-success-bg)" : "var(--kr-bg-subtle)",
                      color: m.enabled ? "var(--kr-success-700)" : "var(--kr-fg-muted)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon name={m.enabled ? "ToggleRight" : "ToggleLeft"} size={20} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)" }}>
                        {m.label}
                      </span>
                      <Badge tone={m.enabled ? "success" : "neutral"} dot>
                        {m.enabled ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                    <div style={{ fontSize: 13, color: "var(--kr-fg-muted)", marginTop: 4 }}>
                      {MODULE_DESCRIPTIONS[m.key] || ""}
                    </div>
                    <div
                      style={{
                        fontFamily: "var(--kr-font-mono)",
                        fontSize: 11,
                        color: "var(--kr-fg-subtle)",
                        marginTop: 4,
                      }}
                    >
                      {m.key}
                    </div>
                  </div>
                </div>
                <Button
                  variant={m.enabled ? "secondary" : "primary"}
                  size="sm"
                  onClick={() => toggle(m)}
                  disabled={savingKey === m.key}
                  icon={m.enabled ? "Square" : "Play"}
                >
                  {savingKey === m.key
                    ? "Guardando…"
                    : m.enabled
                      ? "Desactivar"
                      : "Activar"}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
